import React, { createContext, useContext, useState, useEffect } from "react"
import { authService } from "@/services/auth.service"
import { getAccessToken, clearTokens } from "@/lib/api-client"
import type { User, LoginRequest } from "@/types/api"
import { toast } from "@/hooks/use-toast"

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (data: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user

  // Load user on mount if token exists
  useEffect(() => {
    const loadUser = async () => {
      const token = getAccessToken()
      if (token) {
        try {
          const userData = await authService.getProfile()

          // Check if user is admin (staff or superuser)
          // if (userData.is_staff || userData.is_superuser) {
          //   setUser(userData)
          // } else {
          //   // Not an admin, clear tokens and don't set user
          //   clearTokens()
          //   toast({
          //     title: "Access Denied",
          //     description: "You don't have admin access.",
          //     variant: "destructive",
          //   })
          // }
          setUser(userData)
        } catch (error) {
          // Token is invalid, clear it
          clearTokens()
        }
      }
      setIsLoading(false)
    }

    loadUser()
  }, [])

  const login = async (data: LoginRequest) => {
    try {
      const response = await authService.login(data)

      // Fetch full profile to get is_staff/is_superuser fields
      const fullProfile = await authService.getProfile()

      // Check if user is admin (if profile includes these fields)
      if (fullProfile.is_staff !== undefined || fullProfile.is_superuser !== undefined) {
        if (fullProfile.is_staff || fullProfile.is_superuser) {
          setUser(fullProfile)
          toast({
            title: "Добро пожаловать!",
            description: `Вход выполнен как ${fullProfile.full_name || response.user.phone || "Администратор"}`,
          })
        } else {
          clearTokens()
          throw new Error("У вас нет прав администратора")
        }
      } else {
        // Profile doesn't have admin fields, allow access (for development)
        setUser(fullProfile)
        toast({
          title: "Добро пожаловать!",
          description: `Вход выполнен как ${fullProfile.full_name || response.user.phone || "Пользователь"}`,
        })
      }
    } catch (error: any) {
      clearTokens()
      throw error
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error("Logout error:", error)
    } finally {
      setUser(null)
      clearTokens()
      toast({
        title: "Выход выполнен",
        description: "Вы успешно вышли из системы.",
      })
    }
  }

  const refreshUser = async () => {
    try {
      const userData = await authService.getProfile()
      if (userData.is_staff || userData.is_superuser) {
        setUser(userData)
      } else {
        clearTokens()
        setUser(null)
      }
    } catch (error) {
      clearTokens()
      setUser(null)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
